// Jurisdiction facts and the supported-state registry (NY F1 Runtime, Round 1).
//
// Authority: NY-SCOPE-AUTHORITY-2026-09-06-F1 (Owner ratified 2026-09-06), consumed through the
// frozen docs/plans/NY-F1-NONBLOCKED-RUNTIME-IMPLEMENTATION-PLAN-20260906.md §7.
//
//   - Support is DATA, never a default. A state is supported only where a ratified authority
//     package exists. Exactly one exists today: New York.
//   - The gate is PURE, SYNCHRONOUS and OFFLINE. It never fetches, never infers a state from an
//     address, and NEVER falls back: a property in a state with no package must not be inspected
//     under New York rules and must not receive a generic rule set (PP-06).
//   - The inspector's licensed state and the property's state are two independent facts. They are
//     never conflated, and a selector showing a state's code never means the state is supported.
//   - This module is the single place that names US jurisdictions in the product (Final
//     Implementation Authority §20.2). Round 4 moves the New York authority payload into
//     src/domain/authority/ny/*; the registry keeps pointing at its version string.
export const NY_AUTHORITY_VERSION = 'NY-SCOPE-AUTHORITY-2026-09-06-F1';

// The full US set, so the property-state selector can show any state the inspector actually works
// in. Presence in this list is IDENTITY only — support is decided by SUPPORTED_STATE_REGISTRY.
export const JURISDICTION_CODE_ORDER = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
] as const;

export type JurisdictionCode = (typeof JURISDICTION_CODE_ORDER)[number];

// supported        a ratified authority package exists and is in force
// notYetSupported  no package has been researched or ratified for this state
// excludedByOwner  the Owner excluded the state (PP-03 / PP-04). The reason is recorded in F1 and
//                  is deliberately NOT researched or restated here.
export type JurisdictionStatus = 'supported' | 'notYetSupported' | 'excludedByOwner';

export interface JurisdictionEntry {
  status: JurisdictionStatus;
  // The authority version that governs this state, or null when nothing governs it.
  authorityVersion: string | null;
}

const NOT_YET_SUPPORTED: JurisdictionEntry = Object.freeze({ status: 'notYetSupported', authorityVersion: null });

// Everything not named here is notYetSupported. Nothing is supported by omission.
const EXPLICIT_ENTRIES: Partial<Record<JurisdictionCode, JurisdictionEntry>> = {
  NY: { status: 'supported', authorityVersion: NY_AUTHORITY_VERSION },
  TX: { status: 'excludedByOwner', authorityVersion: null },
  DC: { status: 'excludedByOwner', authorityVersion: null }
};

export const SUPPORTED_STATE_REGISTRY: Readonly<Record<JurisdictionCode, JurisdictionEntry>> = Object.freeze(
  Object.fromEntries(
    JURISDICTION_CODE_ORDER.map((code) => [code, Object.freeze(EXPLICIT_ENTRIES[code] ?? NOT_YET_SUPPORTED)] as const)
  ) as Record<JurisdictionCode, JurisdictionEntry>
);

export function isJurisdictionCode(value: unknown): value is JurisdictionCode {
  return typeof value === 'string' && (JURISDICTION_CODE_ORDER as readonly string[]).includes(value);
}

// A stored / typed state string → a recognised code, or null. Never a guess: an unrecognised
// string (a full state name, a country, an empty field) is null, not a nearest match.
export function normalizeJurisdictionCode(raw: string | null | undefined): JurisdictionCode | null {
  const trimmed = (raw ?? '').trim().toUpperCase();
  return isJurisdictionCode(trimmed) ? trimmed : null;
}

export function jurisdictionStatusOf(code: JurisdictionCode): JurisdictionStatus {
  return SUPPORTED_STATE_REGISTRY[code].status;
}

export type JurisdictionSupport =
  | { kind: 'supported'; authorityVersion: string }
  | { kind: 'inspectorStateMissing' }
  | { kind: 'propertyStateUnconfirmed' }
  | { kind: 'unsupportedPropertyState'; state: JurisdictionCode; status: JurisdictionStatus }
  | { kind: 'stateMismatch'; inspectorState: JurisdictionCode; propertyState: JurisdictionCode };

export interface JurisdictionInput {
  // The state the inspector holds a licence in; null = no inspector profile / not entered.
  inspectorState: JurisdictionCode | null;
  // The property's state, ONLY once the inspector asserted it (a form default is not an assertion).
  // null = unknown or unconfirmed.
  propertyState: JurisdictionCode | null;
  // CK-10 was answered by the Owner on 2026-09-09 (OD-6): the unit-count rule now lives with the
  // other client gates, in src/domain/inspectionContext.ts (legalReviewBlockOf). Support of a STATE
  // is not a function of the building, so this field stays inert here.
  unitCount?: number | null;
}

// Fail-closed, never fall back. There is no default branch that returns `supported`: `supported`
// is produced at exactly one place, and only from a registry entry that says so.
export function jurisdictionSupport(input: JurisdictionInput): JurisdictionSupport {
  if (input.inspectorState === null) return { kind: 'inspectorStateMissing' };
  if (input.propertyState === null) return { kind: 'propertyStateUnconfirmed' };
  const entry = SUPPORTED_STATE_REGISTRY[input.propertyState];
  if (entry.status !== 'supported' || entry.authorityVersion === null) {
    return { kind: 'unsupportedPropertyState', state: input.propertyState, status: entry.status };
  }
  if (input.inspectorState !== input.propertyState) {
    return { kind: 'stateMismatch', inspectorState: input.inspectorState, propertyState: input.propertyState };
  }
  return { kind: 'supported', authorityVersion: entry.authorityVersion };
}

export function jurisdictionSupported(support: JurisdictionSupport): boolean {
  return support.kind === 'supported';
}
